import { TabStyles } from '@tableau/widgets';
import { FormikContextType, useFormikContext } from 'formik';
import * as React from 'react';
import { style } from 'typestyle';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { FormikTextArea, FormikTextBox } from '../../../../commonInfra/components/FormikField';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { UpdateStringServerSettingNames } from '../../../../ts/ServerApi/requests/UpdateStringServerSettingRequest';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { ServerSettingsInstance } from '../../../services/ServerSettings';
import { formError, SettingsTableStyles } from '../../../styles/CommonStyles';
import { wideField } from '../../styles/SiteNameAndIdStyles';

import { ServerSettingsProps } from '../server-settings/ReactServerSettings';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export type SignInNoteSectionProps = ServerSettingsProps<'signInNoteText' | 'signInNoteUrl'>;

export const MAX_SIGN_IN_NOTE_CHAR_LENGTH = 120;
export const MAX_SIGN_IN_URL_LENGTH = 2000;

const SignInNoteLimit = style({
  fontSize: TabStyles.Typography.Sizing.Tiny,
  marginTop: 5,
  marginBottom: 10
});

export function SignInNoteSection(props: SignInNoteSectionProps) {
  const formik = useFormikContext<SignInNoteSectionProps>();

  return FeatureFlags.signInNoteEnabled
    ? (
      <SettingsSection
        title={Messages.signInNoteHeader()}
        description={Messages.signInNoteDescription()}
      >
        <table>
          <tbody>
            {renderNoteTextArea(formik)}
            {renderUrlTextField()}
          </tbody>
        </table>
      </SettingsSection>
    ) : null;
}

function renderNoteTextArea(formik: FormikContextType<SignInNoteSectionProps>) {
  return(
    <tr>
      <td className={SettingsTableStyles.inputLabelCell}>
        {Messages.signInNoteTextField()}
      </td>
      <td>
        <FormikTextArea
          name={'settings.signInNoteText'}
          label={''}
          testId={'sign-in-note-textarea'}
          className={wideField}
          validate={noteValidator}
          rows={3}
        />
        <div
          className={`${formik.errors?.settings?.signInNoteText ? formError : ''} ${SignInNoteLimit}`}
          data-tb-test-id={'sign-in-note-char-counter'}>
          {Messages.signInNoteCharacterCounter({
            COUNT_LOC: Formatters.formatCount(formik.values?.settings?.signInNoteText?.trim()?.length || 0),
            MAXIMUM_LOC: Formatters.formatCount(MAX_SIGN_IN_NOTE_CHAR_LENGTH)
          })}
        </div>
      </td>
    </tr>
  );
}

function renderUrlTextField() {
  return (
    <tr>
      <td className={SettingsTableStyles.inputLabelCell}>
        {Messages.signInNoteUrlField()}
      </td>
      <td>
        <FormikTextBox
          name={'settings.signInNoteUrl'}
          label={''}
          testId={'sign-in-note-url'}
          className={wideField}
          validate={urlValidator}
          eagerValidation={true}
        />
      </td>
    </tr>
  );
}

function noteValidator(value: string): string {
  return value.trim().length > MAX_SIGN_IN_NOTE_CHAR_LENGTH ? 'use this to show error class' : '';
}

function urlValidator(value: string): string {
  if(!value) return ''; // the field is optional

  const httpValidation = value.match(/^[hH][tT][tT][pP][sS]?:\/\/.*/);
  if (!httpValidation) {
    return Messages.signInNoteUrlInvalid();
  } else if (value.length > MAX_SIGN_IN_URL_LENGTH) {
    return Messages.signInNoteUrlCountExceeded();
  } else {
    return '';
  }
}

export const SignInNoteSpec = componentRegistryEntry({
  name: 'SignInNoteSection',
  component: SignInNoteSection,
  onSubmit: async (settings) => {
    await Promise.all([
      ServerSettingsInstance.updateString(UpdateStringServerSettingNames.signInNoteText, settings.signInNoteText),
      ServerSettingsInstance.updateString(UpdateStringServerSettingNames.signInNoteUrl, settings.signInNoteUrl)
    ]);
  }
});
